import React, { useState } from "react";
import { t } from "i18next";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	IconButton,
	Paper,
	Divider,
	Alert
} from "@mui/material";
import {
	Close as CloseIcon,
	Launch as LaunchIcon,
	Description as DocumentIcon
} from "@mui/icons-material";
import MDButton from "components/Basics/MDButton";

const DocumentViewerDialog = ({ open = true, handleClose, documentUrl = "" }) => {
	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="lg"
			fullWidth
			PaperProps={{
				sx: { height: "90vh", maxHeight: "800px" }
			}}
		>
			<DialogTitle>
				<Box display="flex" alignItems="center" justifyContent="space-between">
					<Typography variant="h6" component="div">
						{t("documentViewer.title")}
					</Typography>
					<IconButton
						edge="end"
						color="inherit"
						onClick={handleClose}
						aria-label={t("documentViewer.closeAriaLabel")}
					>
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
				<Box sx={{ flex: 1, p: 2, minHeight: 0 }}>
					{documentUrl ? (
						<Paper
							elevation={2}
							sx={{
								width: "100%",
								height: "100%",
								display: "flex",
								flexDirection: "column",
								overflow: "hidden"
							}}
						>
							<Box
								sx={{
									p: 1,
									backgroundColor: "grey.100",
									display: "flex",
									alignItems: "center",
									gap: 1
								}}
							>
								<DocumentIcon color="action" />
								<Typography
									variant="body2"
									sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}
								>
									{documentUrl}
								</Typography>
								<IconButton
									size="small"
									onClick={() => window.open(documentUrl, "_blank")}
									title={t("documentViewer.openInNewTab")}
								>
									<LaunchIcon fontSize="small" />
								</IconButton>
							</Box>
							<Box sx={{ flex: 1, minHeight: 0 }}>
								<iframe
									src={documentUrl}
									style={{
										width: "100%",
										height: "100%",
										border: "none"
									}}
									title={t("documentViewer.iframeTitle")}
									sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
								/>
							</Box>
						</Paper>
					) : (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								height: "100%",
								color: "text.secondary"
							}}
						>
							<DocumentIcon sx={{ fontSize: 80, mb: 2, color: "action.disabled" }} />
							<Typography variant="h6" gutterBottom>
								{t("documentViewer.noDocumentLoaded")}
							</Typography>

							<Alert severity="info" sx={{ mt: 2, maxWidth: 400 }}>
								{t("documentViewer.supportedFormats")}
							</Alert>
						</Box>
					)}
				</Box>
			</DialogContent>

			<Divider />

			<DialogActions sx={{ p: 2 }}>
				<MDButton variant="outlined" color="info" onClick={() => handleClose?.()}>
					{t("documentViewer.closeButton")}
				</MDButton>
				{documentUrl && (
					<MDButton
						variant="contained"
						color="info"
						startIcon={<LaunchIcon />}
						onClick={() => window.open(documentUrl, "_blank")}
					>
						{t("documentViewer.newTabButton")}
					</MDButton>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default DocumentViewerDialog;
